import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Candy, Loader2, RefreshCw, ShoppingBasket, Store, TrendingUp, Coins, Percent, Receipt, Package } from "lucide-react";

const STORES = [{ v: "all", l: "Alle" }, { v: "falster", l: "Falster" }, { v: "ringsted", l: "Ringsted" }];
const ymd = (d) => d.toISOString().slice(0, 10);
const kr = (n) => Math.round(Number(n || 0)).toLocaleString("da-DK");
const kr2 = (n) => Number(n || 0).toLocaleString("da-DK", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export function Analyse() {
  const [store, setStore] = useState("all");
  const [from, setFrom] = useState(ymd(new Date(Date.now() - 30 * 864e5)));
  const [to, setTo] = useState(ymd(new Date()));
  const [kpi, setKpi] = useState(null);
  const [basket, setBasket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [a, b] = await Promise.all([
        api.get("/analytics/summary", { params: { store, from, to } }),
        api.get("/slik-basket/insights", { params: { store, from, to } }),
      ]);
      setKpi(a.data); setBasket(b.data);
    } catch { toast.error("Kunne ikke hente analyse"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [store, from, to]);

  const backfill = async () => {
    setRunning(true);
    try { await api.post("/slik-basket/backfill", null, { params: { days_back: 30 } }); toast.success("Henter slik-historik i baggrunden — opdatér om et par minutter."); }
    catch { toast.error("Kunne ikke starte"); }
    finally { setRunning(false); }
  };

  const k = kpi || {};
  const b = basket || {};
  const maxBasket = (b.top_products || [])[0]?.baskets || 1;

  const Stat = ({ icon: Icon, label, value, sub, accent }) => (
    <div className={`rounded-2xl border p-5 ${accent ? "border-emerald-200 bg-gradient-to-br from-emerald-500 to-teal-600 text-white" : "border-[#e2e8f0] bg-white"}`}>
      <div className={`flex items-center gap-1.5 text-xs font-medium ${accent ? "text-emerald-50" : "text-slate-400"}`}><Icon className="w-3.5 h-3.5" />{label}</div>
      <p className={`text-3xl font-bold leading-none mt-2 ${accent ? "text-white" : "text-slate-900"}`}>{value}</p>
      {sub && <p className={`text-xs mt-1.5 ${accent ? "text-emerald-50" : "text-slate-400"}`}>{sub}</p>}
    </div>
  );

  return (
    <div className="space-y-6" data-testid="analyse">
      <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-3">
        <div>
          <h4 className="font-heading text-xl font-light text-slate-900 flex items-center gap-2"><TrendingUp className="w-5 h-5 text-emerald-500" /> Analyse</h4>
          <p className="text-sm text-slate-500">Omsætning, profit, kurvstørrelse og kurv-analyse for bland selv slik.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="inline-flex items-center rounded-lg border border-[#e2e8f0] overflow-hidden" data-testid="an-store">
            <Store className="w-4 h-4 text-slate-400 ml-2.5 mr-1" />
            {STORES.map((s) => (
              <button key={s.v} onClick={() => setStore(s.v)} data-testid={`an-store-${s.v}`}
                className={`px-3 py-1.5 text-sm transition-colors ${store === s.v ? "bg-slate-800 text-white" : "text-slate-600 hover:bg-slate-50"}`}>{s.l}</button>
            ))}
          </div>
          <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="h-9 rounded-lg border border-[#e2e8f0] px-2 text-sm" data-testid="an-from" />
          <span className="text-slate-400 text-sm">→</span>
          <input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="h-9 rounded-lg border border-[#e2e8f0] px-2 text-sm" data-testid="an-to" />
          <Button onClick={load} variant="outline" className="rounded-lg gap-1.5 h-9" data-testid="an-refresh"><RefreshCw className="w-4 h-4" />Opdatér</Button>
        </div>
      </div>

      {loading ? (
        <div className="py-20 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-emerald-400" /></div>
      ) : (
        <>
          {/* KPI cards */}
          <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-3">
            <Stat icon={Coins} label="Omsætning" value={`${kr(k.revenue)} kr`} sub="inkl. moms" />
            <Stat icon={TrendingUp} label="Profit" value={`${kr(k.profit)} kr`} sub={`Kostpris ${kr(k.cost)} kr`} accent />
            <Stat icon={Percent} label="Margin" value={`${k.margin}%`} sub="af vareomsætning" />
            <Stat icon={Receipt} label="Antal køb" value={kr(k.orders)} sub={`${k.from} → ${k.to}`} />
            <Stat icon={ShoppingBasket} label="Gns. kurvværdi" value={`${kr2(k.avg_basket_value)} kr`} sub="pr. køb" />
            <Stat icon={Package} label="Gns. kurvstørrelse" value={`${k.avg_basket_size}`} sub="varer pr. køb" />
          </div>

          {/* Top products */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="rounded-2xl border border-[#e2e8f0] bg-white p-5" data-testid="an-top-profit">
              <h5 className="font-medium text-slate-900 mb-3">Største profit (varer)</h5>
              <div className="space-y-1.5">
                {(k.top_profit || []).map((p, i) => (
                  <div key={p.productno} className="flex items-center justify-between gap-2 text-sm">
                    <span className="text-slate-700 truncate"><span className="text-slate-400 mr-2">{i + 1}</span>{p.title}</span>
                    <span className="font-semibold text-emerald-700 whitespace-nowrap">{kr(p.profit)} kr</span>
                  </div>
                ))}
                {(k.top_profit || []).length === 0 && <p className="text-sm text-slate-400">Ingen data i perioden.</p>}
              </div>
            </div>
            <div className="rounded-2xl border border-[#e2e8f0] bg-white p-5" data-testid="an-top-revenue">
              <h5 className="font-medium text-slate-900 mb-3">Største omsætning (varer)</h5>
              <div className="space-y-1.5">
                {(k.top_revenue || []).map((p, i) => (
                  <div key={p.productno} className="flex items-center justify-between gap-2 text-sm">
                    <span className="text-slate-700 truncate"><span className="text-slate-400 mr-2">{i + 1}</span>{p.title}</span>
                    <span className="font-semibold text-slate-900 whitespace-nowrap">{kr(p.revenue)} kr</span>
                  </div>
                ))}
                {(k.top_revenue || []).length === 0 && <p className="text-sm text-slate-400">Ingen data i perioden.</p>}
              </div>
            </div>
          </div>

          {/* Bland selv slik basket analysis */}
          <div className="rounded-2xl border border-rose-100 bg-rose-50/40 p-5" data-testid="an-slik">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-4">
              <h5 className="font-medium text-slate-900 flex items-center gap-2"><Candy className="w-4 h-4 text-rose-500" /> Bland selv slik — hvad køber de ellers?</h5>
              <Button onClick={backfill} disabled={running} size="sm" className="rounded-lg gap-1.5 h-8 bg-slate-800 text-white hover:bg-slate-900 self-start sm:self-auto" data-testid="an-backfill">{running ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <TrendingUp className="w-3.5 h-3.5" />}Hent slik-historik</Button>
            </div>
            {b.total_baskets === 0 ? (
              <p className="text-sm text-slate-500 py-4" data-testid="an-slik-empty">Ingen slik-kurve i perioden endnu. Klik <b>"Hent slik-historik"</b> for at analysere de seneste 30 dage (kører i baggrunden — opdatér derefter).</p>
            ) : (
              <>
                <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-3 mb-5">
                  <div className="rounded-xl bg-gradient-to-br from-rose-500 to-red-600 text-white p-4" data-testid="an-slik-attach">
                    <p className="text-3xl font-bold leading-none">{b.attach_rate}%</p>
                    <p className="text-xs text-rose-50 mt-1.5">køber også andre varer</p>
                  </div>
                  <div className="rounded-xl bg-white border border-[#e2e8f0] p-4"><p className="text-2xl font-bold text-slate-900 leading-none">{kr(b.total_baskets)}</p><p className="text-xs text-slate-400 mt-1.5">slik-kurve</p></div>
                  <div className="rounded-xl bg-white border border-[#e2e8f0] p-4"><p className="text-2xl font-bold text-slate-900 leading-none">{kr(b.total_revenue)} kr</p><p className="text-xs text-slate-400 mt-1.5">omsætning i kurvene</p></div>
                  <div className="rounded-xl bg-white border border-emerald-200 p-4"><p className="text-2xl font-bold text-emerald-700 leading-none">{kr(b.total_profit)} kr</p><p className="text-xs text-slate-400 mt-1.5">profit ({b.margin}%)</p></div>
                  <div className="rounded-xl bg-white border border-[#e2e8f0] p-4"><p className="text-2xl font-bold text-slate-900 leading-none">{kr2(b.avg_basket_value)} kr</p><p className="text-xs text-slate-400 mt-1.5">gns. kurvværdi</p></div>
                  <div className="rounded-xl bg-white border border-[#e2e8f0] p-4" data-testid="an-slik-mersalg"><p className="text-2xl font-bold text-slate-900 leading-none">{kr(b.other_revenue)} kr</p><p className="text-xs text-slate-400 mt-1.5">mersalg (andre varer)</p></div>
                </div>
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                  <div className="lg:col-span-2" data-testid="an-slik-products">
                    <p className="text-sm font-medium text-slate-700 mb-3">Mest købt sammen med bland selv slik</p>
                    <div className="space-y-2">
                      {(b.top_products || []).map((p, i) => (
                        <div key={p.productno} className="flex items-center gap-3">
                          <span className="text-xs text-slate-400 w-5 text-right">{i + 1}</span>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between gap-2">
                              <span className="text-sm text-slate-800 truncate">{p.title}</span>
                              <span className="text-sm font-semibold text-slate-900 whitespace-nowrap">{kr(p.baskets)} kurve · {kr(p.revenue)} kr</span>
                            </div>
                            <div className="mt-1 h-1.5 bg-rose-100 rounded-full overflow-hidden"><div className="h-full bg-rose-400 rounded-full" style={{ width: `${Math.max(4, (p.baskets / maxBasket) * 100)}%` }} /></div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div data-testid="an-slik-cats">
                    <p className="text-sm font-medium text-slate-700 mb-3">Top varegrupper</p>
                    <div className="space-y-1.5">
                      {(b.top_categories || []).map((c) => (
                        <div key={c.category} className="flex items-center justify-between gap-2 text-sm"><span className="text-slate-700 truncate">{c.category}</span><span className="font-semibold text-slate-900">{kr(c.baskets)}</span></div>
                      ))}
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
          <p className="text-xs text-slate-400">Omsætning og antal køb er fra POS. Profit, margin og gns. kurvstørrelse er baseret på synkroniserede varelinjer og dækker kun perioden med synkroniseret salgsdata (kan være 0 for ældre datoer). Periode: {k.from} → {k.to}.</p>
        </>
      )}
    </div>
  );
}
